import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';

const TeamsOverlay = () => {
  const [auctionData, setAuctionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSliderPaused, setIsSliderPaused] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const inviteCode = urlParams.get('i');

  useEffect(() => {
    if (!inviteCode) {
      setLoading(false);
      return;
    }

    fetchAuctionData();
    
    // Auto-refresh every 5 seconds
    const interval = setInterval(fetchAuctionData, 5000);
    
    return () => clearInterval(interval);
  }, [inviteCode]);

  // Auto-slide effect for slider - moved to top level to avoid hooks rules violation
  useEffect(() => {
    if (!auctionData || !auctionData.auction_details || !auctionData.auction_details.teams) return;
    
    const teams = auctionData.auction_details.teams;
    const teamsPerSlide = 5;
    const totalSlides = Math.ceil(teams.length / teamsPerSlide);
    const showSlider = teams.length > 10;
    
    if (!showSlider || totalSlides <= 1 || isSliderPaused) return;

    const autoSlideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 4000); // Auto-slide every 4 seconds

    return () => clearInterval(autoSlideInterval);
  }, [auctionData, isSliderPaused]);

  const fetchAuctionData = async () => {
    try {
      if (!inviteCode) return;

      const response = await fetch(global.config.app.backend_api_url + 'auction/get_current_auction_status_by_invite_code/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invite_code: inviteCode
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAuctionData(data);
      
      // Get current player from the data if available
      if (data.current_player) {
        setCurrentPlayer(data.current_player);
      }
    } catch (error) {
      console.error('Error fetching auction data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          color: 'white',
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!auctionData || !inviteCode) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          color: 'white',
          fontSize: '24px',
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          Invalid invite code or no auction data
        </div>
      </div>
    );
  }

  const auctionMain = auctionData.auction_details.main[0];
  const teams = auctionData.auction_details.teams;
  const topSell = auctionData.auction_details.top_sell || [];
  const recentSell = auctionData.auction_details.recent_sell || [];
  
  // Slider logic
  const teamsPerSlide = 5; // Show 5 teams per slide
  const totalSlides = Math.ceil(teams.length / teamsPerSlide);
  const showSlider = teams.length > 10;
  
  const getCurrentSlideTeams = () => {
    const startIndex = currentSlide * teamsPerSlide;
    const endIndex = Math.min(startIndex + teamsPerSlide, teams.length);
    return teams.slice(startIndex, endIndex);
  };
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      width: '400px',
      background: 'rgba(0,0,0,0.85)',
      borderRadius: '12px',
      padding: '20px',
      color: 'white',
      fontFamily: 'Arial, sans-serif',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.1)',
      zIndex: 9999
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '20px',
        paddingBottom: '15px',
        borderBottom: '2px solid rgba(255,255,255,0.2)'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#00ff88',
          textShadow: '0 0 10px rgba(0,255,136,0.5)'
        }}>
          🏏 {auctionMain.series_name}
        </h3>
        <div style={{
          fontSize: '14px',
          color: '#ccc',
          marginTop: '5px'
        }}>
          Teams: {auctionMain.no_of_teams} | Players: {auctionMain.count_players_in_auction}
        </div>
      </div>

      {/* Team Summary - Slider or Table */}
      {showSlider ? (
        <div
          onMouseEnter={() => setIsSliderPaused(true)}
          onMouseLeave={() => setIsSliderPaused(false)}
        >
          {/* Slider Navigation */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
            padding: '0 5px'
          }}>
            <button
              onClick={prevSlide}
              style={{
                background: 'rgba(0,255,136,0.2)',
                border: '1px solid #00ff88',
                color: '#00ff88',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0,255,136,0.3)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0,255,136,0.2)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ‹
            </button>
            
            <div style={{
              display: 'flex',
              gap: '5px',
              alignItems: 'center'
            }}>
              <span style={{
                fontSize: '12px',
                color: '#ccc'
              }}>
                {currentSlide + 1} / {totalSlides}
              </span>
              <div style={{
                display: 'flex',
                gap: '3px'
              }}>
                {Array.from({ length: totalSlides }, (_, i) => (
                  <div
                    key={i}
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: i === currentSlide ? '#00ff88' : 'rgba(255,255,255,0.3)',
                      transition: 'all 0.3s ease',
                      boxShadow: i === currentSlide && !isSliderPaused ? '0 0 8px rgba(0,255,136,0.6)' : 'none'
                    }}
                  />
                ))}
              </div>
              {!isSliderPaused && (
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#00ff88',
                  animation: 'pulse 1s infinite',
                  marginLeft: '5px'
                }} />
              )}
            </div>
            
            <button
              onClick={nextSlide}
              style={{
                background: 'rgba(0,255,136,0.2)',
                border: '1px solid #00ff88',
                color: '#00ff88',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0,255,136,0.3)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0,255,136,0.2)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ›
            </button>
          </div>

          {/* Slider Content */}
          <div style={{
            height: '300px',
            overflow: 'hidden'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{
                  background: 'rgba(255,255,255,0.1)',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                }}>
                  <th style={{
                    padding: '8px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#00ff88',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Team</th>
                  <th style={{
                    padding: '8px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#00ff88',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Max Bid</th>
                  <th style={{
                    padding: '8px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#00ff88',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Players</th>
                </tr>
              </thead>
              <tbody>
                {getCurrentSlideTeams().map((team, index) => {
                  const currentPlayers = parseInt(team.current_players_count);
                  const maxPlayers = parseInt(team.team_max_playes_count);
                  const progressPercentage = (currentPlayers / maxPlayers) * 100;
                  
                  const isTeamFull = progressPercentage === 100;
                  const isBidExceeded = currentPlayer && 
                    parseFloat(currentPlayer.amount) >= parseFloat(team.max_bid_for_a_player);
                  
                  let rowStyle = {
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.3s ease'
                  };
                  
                  if (isTeamFull) {
                    rowStyle.background = 'rgba(0,255,136,0.2)';
                    rowStyle.borderLeft = '4px solid #00ff88';
                    rowStyle.boxShadow = '0 0 15px rgba(0,255,136,0.3)';
                  } else if (isBidExceeded) {
                    rowStyle.background = 'rgba(255,0,100,0.2)';
                    rowStyle.borderLeft = '4px solid #ff0064';
                    rowStyle.boxShadow = '0 0 15px rgba(255,0,100,0.3)';
                  }
                  
                  return (
                    <tr key={index} style={rowStyle}>
                      <td style={{
                        padding: '10px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <img
                          src={team.team_logo && team.team_logo.trim() !== '' ? team.team_logo : '/placeholder.svg'}
                          alt={team.team_name + ' logo'}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            background: '#fff',
                            border: '1px solid rgba(255,255,255,0.2)'
                          }}
                          onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
                        />
                        <span style={{
                          fontWeight: '600',
                          fontSize: '13px',
                          color: isTeamFull ? '#00ff88' : isBidExceeded ? '#ff0064' : 'white'
                        }}>
                          {team.team_name}
                        </span>
                      </td>
                      <td style={{
                        padding: '10px 8px',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: '#00ff88',
                        fontSize: '13px'
                      }}>
                        {team.max_bid_for_a_player}
                      </td>
                      <td style={{
                        padding: '10px 8px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}>
                          <div style={{
                            width: '60px',
                            height: '8px',
                            background: 'rgba(255,255,255,0.2)',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${Math.min(progressPercentage, 100)}%`,
                              height: '100%',
                              background: progressPercentage >= 100 ? '#00ff88' : progressPercentage >= 80 ? '#ffaa00' : '#00aaff',
                              borderRadius: '4px',
                              transition: 'all 0.3s ease',
                              boxShadow: progressPercentage >= 100 ? '0 0 10px rgba(0,255,136,0.5)' : 'none'
                            }} />
                          </div>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: progressPercentage >= 100 ? '#00ff88' : 'white',
                            minWidth: '35px'
                          }}>
                            {currentPlayers}/{maxPlayers}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Original Table View for 10 or fewer teams */
        <div style={{
          maxHeight: '500px',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.3) transparent'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead>
              <tr style={{
                background: 'rgba(255,255,255,0.1)',
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}>
                <th style={{
                  padding: '8px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#00ff88',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Team</th>
                <th style={{
                  padding: '8px',
                  textAlign: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#00ff88',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Max Bid</th>
                <th style={{
                  padding: '8px',
                  textAlign: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#00ff88',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Players</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team, index) => {
                const currentPlayers = parseInt(team.current_players_count);
                const maxPlayers = parseInt(team.team_max_playes_count);
                const progressPercentage = (currentPlayers / maxPlayers) * 100;
                
                const isTeamFull = progressPercentage === 100;
                const isBidExceeded = currentPlayer && 
                  parseFloat(currentPlayer.amount) >= parseFloat(team.max_bid_for_a_player);
                
                let rowStyle = {
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  transition: 'all 0.3s ease'
                };
                
                if (isTeamFull) {
                  rowStyle.background = 'rgba(0,255,136,0.2)';
                  rowStyle.borderLeft = '4px solid #00ff88';
                  rowStyle.boxShadow = '0 0 15px rgba(0,255,136,0.3)';
                } else if (isBidExceeded) {
                  rowStyle.background = 'rgba(255,0,100,0.2)';
                  rowStyle.borderLeft = '4px solid #ff0064';
                  rowStyle.boxShadow = '0 0 15px rgba(255,0,100,0.3)';
                }
                
                return (
                  <tr key={index} style={rowStyle}>
                    <td style={{
                      padding: '10px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <img
                        src={team.team_logo && team.team_logo.trim() !== '' ? team.team_logo : '/placeholder.svg'}
                        alt={team.team_name + ' logo'}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          background: '#fff',
                          border: '1px solid rgba(255,255,255,0.2)'
                        }}
                        onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
                      />
                      <span style={{
                        fontWeight: '600',
                        fontSize: '13px',
                        color: isTeamFull ? '#00ff88' : isBidExceeded ? '#ff0064' : 'white'
                      }}>
                        {team.team_name}
                      </span>
                    </td>
                    <td style={{
                      padding: '10px 8px',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: '#00ff88',
                      fontSize: '13px'
                    }}>
                      {team.max_bid_for_a_player}
                    </td>
                    <td style={{
                      padding: '10px 8px',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}>
                        <div style={{
                          width: '60px',
                          height: '8px',
                          background: 'rgba(255,255,255,0.2)',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${Math.min(progressPercentage, 100)}%`,
                            height: '100%',
                            background: progressPercentage >= 100 ? '#00ff88' : progressPercentage >= 80 ? '#ffaa00' : '#00aaff',
                            borderRadius: '4px',
                            transition: 'all 0.3s ease',
                            boxShadow: progressPercentage >= 100 ? '0 0 10px rgba(0,255,136,0.5)' : 'none'
                          }} />
                        </div>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: progressPercentage >= 100 ? '#00ff88' : 'white',
                          minWidth: '35px'
                        }}>
                          {currentPlayers}/{maxPlayers}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer Stats */}
      <div style={{
        marginTop: '15px',
        paddingTop: '15px',
        borderTop: '1px solid rgba(255,255,255,0.2)',
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '12px',
        color: '#ccc'
      }}>
        <div>
          <span style={{ color: '#00ff88' }}>Sold:</span> {auctionMain.sold_count}
        </div>
        <div>
          <span style={{ color: '#ff0064' }}>Unsold:</span> {auctionMain.unsold_count}
        </div>
        <div>
          <span style={{ color: '#ffaa00' }}>Pending:</span> {auctionMain.pending_count}
        </div>
      </div>

      {/* Scrolling Ticker for Top Sell and Recent Sell */}
      {(topSell.length > 0 || recentSell.length > 0) && 1==2 && (
        <div style={{
          marginTop: '15px',
          paddingTop: '15px',
          borderTop: '1px solid rgba(255,255,255,0.2)',
          overflow: 'hidden',
          position: 'relative',
          height: '40px',
          background: 'rgba(0,255,136,0.05)',
          borderRadius: '6px'
        }}>
          <div className="scrolling-ticker" style={{
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            gap: '30px'
          }}>
            {/* Top Sell Section */}
            {topSell.length > 0 && (
              <>
                <span style={{
                  color: '#ffaa00',
                  fontWeight: 'bold',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginRight: '10px'
                }}>
                  🏆 TOP SELL:
                </span>
                {topSell.map((player, index) => (
                  <span key={`top-${index}`} style={{
                    color: 'white',
                    fontSize: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{ color: '#00ff88', fontWeight: 'bold' }}>
                      {player.full_name || 'N/A'}
                    </span>
                    <span style={{ color: '#ccc' }}>→</span>
                    <span style={{ color: '#ffaa00', fontWeight: 'bold' }}>
                      {player.auction_sold_amount || '0'}
                    </span>
                    <span style={{ color: '#888', fontSize: '10px' }}>
                      ({player.team_name || 'N/A'})
                    </span>
                    <span style={{ color: '#666', margin: '0 10px' }}>•</span>
                  </span>
                ))}
              </>
            )}
            
            {/* Recent Sell Section */}
            {recentSell.length > 0 && 1==2  && (
              <>
                <span style={{
                  color: '#00aaff',
                  fontWeight: 'bold',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginLeft: '20px',
                  marginRight: '10px'
                }}>
                  ⚡ RECENT:
                </span>
                {recentSell.map((player, index) => (
                  <span key={`recent-${index}`} style={{
                    color: 'white',
                    fontSize: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{ color: '#00ff88', fontWeight: 'bold' }}>
                      {player.full_name || 'N/A'}
                    </span>
                    <span style={{ color: '#ccc' }}>→</span>
                    <span style={{ color: '#00aaff', fontWeight: 'bold' }}>
                      {player.auction_sold_amount || '0'}
                    </span>
                    <span style={{ color: '#888', fontSize: '10px' }}>
                      ({player.team_name || 'N/A'})
                    </span>
                    <span style={{ color: '#666', margin: '0 10px' }}>•</span>
                  </span>
                ))}
              </>
            )}
            
            {/* Duplicate content for seamless loop */}
            {topSell.length > 0 && 1==2 && (
              <>
                <span style={{
                  color: '#ffaa00',
                  fontWeight: 'bold',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginLeft: '20px',
                  marginRight: '10px'
                }}>
                  🏆 TOP SELL:
                </span>
                {topSell.map((player, index) => (
                  <span key={`top-dup-${index}`} style={{
                    color: 'white',
                    fontSize: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{ color: '#00ff88', fontWeight: 'bold' }}>
                      {player.full_name || 'N/A'}
                    </span>
                    <span style={{ color: '#ccc' }}>→</span>
                    <span style={{ color: '#ffaa00', fontWeight: 'bold' }}>
                      {player.auction_sold_amount || '0'}
                    </span>
                    <span style={{ color: '#888', fontSize: '10px' }}>
                      ({player.team_name || 'N/A'})
                    </span>
                    <span style={{ color: '#666', margin: '0 10px' }}>•</span>
                  </span>
                ))}
              </>
            )}
            
            {recentSell.length > 0 && (
              <>
                <span style={{
                  color: '#00aaff',
                  fontWeight: 'bold',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginLeft: '20px',
                  marginRight: '10px'
                }}>
                  ⚡ RECENT:
                </span>
                {recentSell.map((player, index) => (
                  <span key={`recent-dup-${index}`} style={{
                    color: 'white',
                    fontSize: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{ color: '#00ff88', fontWeight: 'bold' }}>
                      {player.full_name || 'N/A'}
                    </span>
                    <span style={{ color: '#ccc' }}>→</span>
                    <span style={{ color: '#00aaff', fontWeight: 'bold' }}>
                      {player.auction_sold_amount || '0'}
                    </span>
                    <span style={{ color: '#888', fontSize: '10px' }}>
                      ({player.team_name || 'N/A'})
                    </span>
                    <span style={{ color: '#666', margin: '0 10px' }}>•</span>
                  </span>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes pulse {
          0% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(0.8);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes scroll-ticker {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .scrolling-ticker {
          animation: scroll-ticker 2s linear infinite;
        }
        .scrolling-ticker:hover {
          animation-play-state: paused;
        }
      `}} />
    </div>
  );
};

export default TeamsOverlay;
